import React from "react";
import { inject, observer } from "mobx-react";

import {
    Badge,
    createStyles,
    Divider,
    Hidden,
    IconButton,
    List,
    Theme,
    Typography,
    withStyles,
    Zoom,
} from "@material-ui/core";

import { ChevronLeftRounded, ChevronRightRounded } from "@material-ui/icons";

import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import { IContextStoreProps } from "../../stores/IContextStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import { AppIconType } from "../shared/AppIconType";

import i18n from "../../services/localization/i18n";
import TooltipIconButton from "../shared/TooltipIconButton";

import { AddonRuntimeInfo } from "../../domain/api/addons/AddonRuntimeInfo";
import { PredefinedHostType } from "../../domain/api/addons/PredefinedHostType";
import { INavigationStoreProps } from "../../stores/INavigationStoreProps";

import PredefinedRoute from "../enums/PredefinedRoute";
import EndMeetButton from "./EndMeetButton";

import configStore from "../../stores/ConfigStore";
import { getLocalizedText, isTeamsAddonMode } from "../../utils";
import { IPanelStoreProps } from "../../stores/IPanelStoreProps";
import { PanelAddon } from "../../stores/PanelAddon";
import OptionsDialog from "../settings/OptionsDialog";
import { PredefinedAchievementCheckpoint } from "../../services/api/PredefinedAchievementCheckpoint";
import { PredefinedAchievementScenario } from "../../services/api/PredefinedAchievementScenario";
import { AchivementsRequest } from "../../services/api/AchievementsRequest";
import achievementsService from "../../services/api/AchievementsService";
import { ClientTelemetryEvent } from "../../services/tx/ClientTelemetryEvent";
import telemetryDataService from "../../services/tx/TelemetryDataService";
import ReactivateButton from "./ReactivateButton";

const styles = (theme: Theme) =>
    createStyles({
        activeToolbar: {
            "&::focus-visible": {
                outline: "none",
            },
            "&:focus": {
                outline: "none",
            },
            borderBottomColor: theme.palette.error.main,
            borderBottomStyle: "solid",
            borderBottomWidth: "thick",
        },
        activePanelAddon: {
            "&::focus-visible": {
                outline: "none",
            },
            "&:focus": {
                outline: "none",
            },
            borderBottomColor: theme.palette.secondary.main,
            borderBottomStyle: "solid",
            borderBottomWidth: "thick",
        },
        addons: {
            display: "flex",
            flexDirection: "row",
        },
        auxPanel: {
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
        },
        divider: {
            height: theme.spacing(4),
            marginLeft: theme.spacing(),
            marginRight: theme.spacing(),
            width: theme.spacing(0.5),
        },
        drawerIconImage: {
            height: 24,
            marginLeft: 8,
        },
        hidden: {
            width: 0,
        },
        inactiveToolbarItem: {
            borderBottomColor: "transparent",
            borderBottomStyle: "solid",
            borderBottomWidth: "thick",
        },
        leftPanel: {
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
        },
        panelAddons: {
            display: "flex",
            flexDirection: "row",
            width: "auto",
        },
        rightPanel: {
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
        },

        root: {
            [theme.breakpoints.down("sm")]: {
                left: 0,
                right: 0,
                top: 0,
                backgroundColor:
                    configStore.me!.theme === "dark"
                        ? "rgba(0, 0, 0, 0.5)"
                        : "rgba(255, 255, 255, 0.6)",
            },
            "&:hover": {
                backgroundColor:
                    configStore.me!.theme === "dark"
                        ? "rgba(0, 0, 0, 0.5)"
                        : "rgba(255, 255, 255, 0.6)",
            },
            backgroundColor:
                configStore.me!.theme === "dark"
                    ? "rgba(0, 0, 0, 0.1)"
                    : "rgba(255, 255, 255, 0.3)",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            userSelect: "none",
            width: "auto",
        },
        tenant: {
            marginLeft: theme.spacing(2),
            height: theme.spacing(8),
        },
        title: {
            marginLeft: theme.spacing(2),
            padding: 0,
        },
        toolbarIcon: {
            [theme.breakpoints.down("lg")]: {
                padding: theme.spacing(0.5),
            },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "0 8px",
            ...theme.mixins.toolbar,
        },
        topbar: {
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "space-between",
            height: theme.spacing(11),
            position: "absolute",
            right: theme.spacing(1),
            top: theme.spacing(1),
            zIndex: 1,
        },
    });

interface IMiniBarProps {
    classes: {
        activeToolbar: string;
        activePanelAddon: string;
        addons: string;
        auxPanel: string;
        divider: string;
        drawerIconImage: string;
        hidden: string;
        inactiveToolbarItem: string;
        leftPanel: string;
        panelAddons: string;
        rightPanel: string;
        root: string;
        tenant: string;
        title: string;
        topbar: string;
        toolbarIcon: string;
    };

    ending: boolean;

    onInviteShared: () => void;
    onEndMeetSuggested: () => void;
    onFeedbackRequested: () => void;
    onSnapshotRequested: (selected: boolean) => void;

    snapshotSelected: boolean;

    theme: Theme;
}

declare type CollapsableState =
    | "collapsing"
    | "collapsed"
    | "expanding"
    | "expanded";

interface IMiniBarState {
    dashboardTooltip: boolean;
    fullscreenLeaveTooltip: boolean;
    feedbackTooltip: boolean;
    inviteTooltip: boolean;
    settingsTooltip: boolean;
    snapshotTooltip: boolean;
    sonarTooltip: boolean;
    teamsConfigVisible: boolean;

    settingsVisible: boolean;

    collapsed: boolean;
    canvasState: CollapsableState;
    panelState: CollapsableState;

    snapshotSelected: boolean;
}

@inject(PredefinedStoreName.CONTEXT)
@inject(PredefinedStoreName.NAVIGATION)
@inject(PredefinedStoreName.CONFIG)
@inject(PredefinedStoreName.PANEL)
@observer
class MiniBar extends React.Component<
    IMiniBarProps &
        IContextStoreProps &
        IConfigStoreProps &
        INavigationStoreProps &
        IPanelStoreProps,
    IMiniBarState
> {
    private lastTooltipRequest?: number;

    private sonarInTimeout?: number;
    private sonarOutTimeout?: number;
    private feedbackInTimeout?: number;
    private feedbackOutTimeout?: number;
    private settingInTimeout?: number;
    private settingOutTimeout?: number;
    private inviteInTimeout?: number;
    private inviteOutTimeout?: number;
    private dashboardInTimeout?: number;
    private dashboardOutTimeout?: number;
    private snapshotInTimeout?: number;
    private snapshotOutTimeout?: number;

    /**
     * Creates an instance of MiniBar.
     * @param {(IMiniBarProps & IContextStoreProps & IConfigStoreProps & IPanelStoreProps)} props
     * @memberof MiniBar
     */
    constructor(
        props: IMiniBarProps &
            IContextStoreProps &
            IConfigStoreProps &
            INavigationStoreProps &
            IPanelStoreProps
    ) {
        super(props);

        this.state = {
            dashboardTooltip: false,
            fullscreenLeaveTooltip: false,
            inviteTooltip: false,
            settingsTooltip: false,
            sonarTooltip: false,
            teamsConfigVisible: false,
            feedbackTooltip: false,
            settingsVisible: false,
            collapsed: false,
            canvasState: "expanded",
            panelState: "expanded",
            snapshotTooltip: false,
            snapshotSelected: props.snapshotSelected,
        };
    }

    componentDidUpdate(prevProps: IMiniBarProps) {
        if (prevProps.snapshotSelected !== this.props.snapshotSelected) {
            this.setState({
                snapshotSelected: this.props.snapshotSelected,
            });
        }
    }

    public componentWillUnmount() {
        this.clearTimeouts();
    }

    public render() {
        const { classes } = this.props;
        const { tenantName, tenantLogo } = this.props.contextStore!;

        return (
            <div className={classes.topbar}>
                {this.getToolbar()}
                {tenantLogo && (
                    <img
                        src={tenantLogo}
                        className={classes.tenant}
                        alt={tenantName}
                    />
                )}
            </div>
        );
    }

    private getCanvasAddons = () => {
        const { classes } = this.props;
        const { addonsInfo } = this.props.contextStore!;

        const canvasAddons = addonsInfo.filter(
            (p) => p.host.type === PredefinedHostType.CANVAS
        );

        if (canvasAddons.length < 2) {
            return <></>;
        }

        return (
            <>
                {this.state.panelState !== "collapsed" && (
                    <Divider
                        orientation="vertical"
                        className={classes.divider}
                    />
                )}
                <List component="nav" className={classes.addons}>
                    {canvasAddons.map((addon) =>
                        this.getNavigationItem(addon, true)
                    )}
                </List>
            </>
        );
    };

    private handleSnapshotClicked = () => {
        this.props.panelStore!.unselectAll();

        const selected = !this.state.snapshotSelected;
        this.setState({
            snapshotSelected: selected,
        });
        this.props.onSnapshotRequested(selected);
    };

    private getToolbar = () => {
        const {
            classes,
            ending,
            onEndMeetSuggested,
            onFeedbackRequested,
            theme,
        } = this.props;
        const { activeRoute } = this.props.navigationStore!;

        const {
            activePanelAddon,
            toolbarIcon,
            inactiveToolbarItem,
        } = this.props.classes!;

        const selectedSettingsRoute = activeRoute === PredefinedRoute.OPTIONS;

        const { finishedMeet, shellVisible } = this.props.contextStore!;

        return (
            <div className={classes.root} hidden={!shellVisible}>
                <div className={classes.leftPanel}>
                    <IconButton onClick={this.onMenuCollapse}>
                        {this.state.collapsed && <ChevronLeftRounded />}
                        {!this.state.collapsed && <ChevronRightRounded />}
                    </IconButton>

                    {finishedMeet && (
                        <Typography
                            variant="h6"
                            noWrap={true}
                            color="error"
                            className={classes.title}
                        >
                            {i18n.t("meeting_finished_title")}
                        </Typography>
                    )}

                    {isTeamsAddonMode && (
                        <div
                            className={classes!.toolbarIcon}
                            style={{
                                marginRight: theme.spacing(2),
                            }}
                        >
                            <TooltipIconButton
                                button={true}
                                icon={AppIconType.Dashboard}
                                placement="left"
                                title={i18n.t("minibar_dashboard_tooltip")}
                                onClick={this.navigateToDashboard}
                                open={this.state.dashboardTooltip}
                            />
                            {!this.state.collapsed && (
                                <Hidden xsDown={true}>
                                    <Zoom in={!this.state.collapsed}>
                                        <Typography
                                            variant="caption"
                                            style={{ marginTop: -12 }}
                                            color="textPrimary"
                                        >
                                            {i18n.t("minibar_dashboard_title")}
                                        </Typography>
                                    </Zoom>
                                </Hidden>
                            )}
                        </div>
                    )}

                    {!finishedMeet && (
                        <div
                            className={
                                this.state.panelState !== "collapsed"
                                    ? classes!.toolbarIcon
                                    : classes.hidden
                            }
                        >
                            <Zoom in={!this.state.collapsed}>
                                <TooltipIconButton
                                    button={true}
                                    icon={AppIconType.PersonAdd}
                                    placement="bottom"
                                    title={i18n.t(
                                        "layout_toolbar_invite_tooltip"
                                    )}
                                    onClick={this.shareInvite}
                                    open={this.state.inviteTooltip}
                                />
                            </Zoom>
                            {!this.state.collapsed && (
                                <Hidden xsDown={true}>
                                    <Typography
                                        variant="caption"
                                        style={{ marginTop: -12 }}
                                        color="textPrimary"
                                    >
                                        {i18n.t("layout_toolbar_invite")}
                                    </Typography>
                                </Hidden>
                            )}
                        </div>
                    )}

                    {!finishedMeet && !this.state.collapsed && (
                        <Hidden lgDown={true}>
                            <Typography
                                variant="h6"
                                noWrap={true}
                                color="textPrimary"
                                className={classes.title}
                            >
                                {i18n.t("layout_title_text")}
                            </Typography>
                        </Hidden>
                    )}
                </div>
                <div className={classes.rightPanel}>
                    {this.getCanvasAddons()}

                    {this.getPanelAddons()}

                    {!finishedMeet && this.state.panelState !== "collapsed" && (
                        <Divider
                            orientation="vertical"
                            className={classes.divider}
                            style={{
                                marginRight: 16,
                            }}
                        />
                    )}
                </div>
                <div className={classes.auxPanel}>
                    <div
                        className={
                            this.state.snapshotSelected
                                ? `${toolbarIcon} ${activePanelAddon}`
                                : `${toolbarIcon} ${inactiveToolbarItem}`
                        }
                    >
                        <div className={classes!.toolbarIcon}>
                            <Zoom in={true}>
                                <TooltipIconButton
                                    button={true}
                                    icon={AppIconType.Snapshot}
                                    placement="bottom"
                                    title={i18n.t("layout_sidebar_snapshot")}
                                    onClick={this.handleSnapshotClicked}
                                    open={this.state.snapshotTooltip}
                                />
                            </Zoom>
                            {!this.state.collapsed && (
                                <Hidden xsDown={true}>
                                    <Typography
                                        variant="caption"
                                        style={{ marginTop: -12 }}
                                        color="textSecondary"
                                    >
                                        {i18n.t("layout_sidebar_snapshot")}
                                    </Typography>
                                </Hidden>
                            )}
                        </div>
                    </div>
                </div>

                <div
                    className={
                        this.state.panelState !== "collapsed"
                            ? classes.auxPanel
                            : classes.hidden
                    }
                >
                    <Divider
                        orientation="vertical"
                        className={classes.divider}
                        style={{
                            marginRight: 16,
                        }}
                    />
                    {!finishedMeet && (
                        <Hidden smDown={true}>
                            <div className={classes!.toolbarIcon}>
                                <Zoom in={!this.state.collapsed}>
                                    <TooltipIconButton
                                        button={true}
                                        icon={AppIconType.Feedback}
                                        placement="bottom"
                                        title={i18n.t(
                                            "layout_sidebar_feedback"
                                        )}
                                        onClick={onFeedbackRequested}
                                        open={this.state.feedbackTooltip}
                                    />
                                </Zoom>
                                {!this.state.collapsed && (
                                    <Hidden xsDown={true}>
                                        <Typography
                                            variant="caption"
                                            style={{ marginTop: -12 }}
                                            color="textSecondary"
                                        >
                                            {i18n.t("layout_sidebar_rateus")}
                                        </Typography>
                                    </Hidden>
                                )}
                            </div>
                        </Hidden>
                    )}

                    {!finishedMeet && (
                        <div className={classes!.toolbarIcon}>
                            <Zoom in={!this.state.collapsed}>
                                <TooltipIconButton
                                    button={true}
                                    icon={AppIconType.Help}
                                    placement="bottom"
                                    onClick={this.onHelpRequested}
                                    title={i18n.t("layout_sidebar_explorer")}
                                    open={this.state.sonarTooltip}
                                />
                            </Zoom>
                            {!this.state.collapsed && (
                                <Hidden xsDown={true}>
                                    <Typography
                                        variant="caption"
                                        style={{ marginTop: -12 }}
                                        color="textPrimary"
                                    >
                                        {i18n.t("layout_sidebar_explorer")}
                                    </Typography>
                                </Hidden>
                            )}
                        </div>
                    )}

                    {!finishedMeet && (
                        <div
                            className={
                                selectedSettingsRoute
                                    ? `${classes.toolbarIcon} ${classes.activeToolbar}`
                                    : classes!.toolbarIcon
                            }
                        >
                            <Hidden xsDown={true}>
                                <Zoom in={!this.state.collapsed}>
                                    <TooltipIconButton
                                        button={true}
                                        icon={AppIconType.Settings}
                                        placement="bottom"
                                        title={i18n.t(
                                            "layout_sidebar_settings"
                                        )}
                                        onClick={() =>
                                            this.setState({
                                                settingsVisible: true,
                                            })
                                        }
                                        open={this.state.settingsTooltip}
                                    />
                                </Zoom>
                                {!this.state.collapsed && (
                                    <Hidden xsDown={true}>
                                        <Typography
                                            variant="caption"
                                            style={{ marginTop: -12 }}
                                            color="textSecondary"
                                        >
                                            {i18n.t("layout_sidebar_settings")}
                                        </Typography>
                                    </Hidden>
                                )}
                            </Hidden>
                        </div>
                    )}
                </div>

                {!finishedMeet && (
                    <EndMeetButton
                        onEndMeetSuggested={onEndMeetSuggested}
                        ending={ending}
                        collapsed={this.state.collapsed}
                    />
                )}

                {finishedMeet && <ReactivateButton />}

                {this.state.settingsVisible && (
                    <OptionsDialog
                        open={true}
                        onClose={() =>
                            this.setState({ settingsVisible: false })
                        }
                    />
                )}

                {this.showOnboardingTooltips()}
            </div>
        );
    };

    private navigateToDashboard = () => {
        const { resourceId, resourceType } = this.props.contextStore!;

        const scenario = isTeamsAddonMode
            ? PredefinedAchievementScenario.TEAMS_ONBOARDING
            : PredefinedAchievementScenario.MEET_GENERAL;

        const achievement: AchivementsRequest = {
            scenario: scenario,
            checkpoint: PredefinedAchievementCheckpoint.MINIBAR_DASHBOARD_CLICK,
            resourceId: resourceId!,
            resourceType: resourceType!,
        };
        achievementsService.postAsync(achievement).then(() => {
            sessionStorage.removeItem("meet-out-code");
            window.location.href = `${configStore.hosts.web}/teams`;
        });

        const txEvent = {
            correlationId: "N/A'",
            environment: configStore.environment,
            name: "spa_meetApp_dahboardClick",
            sentryEventId: "N/A",
            silent: false,
            source: "meetApp",
            success: true,
            timeToComplete: 0,
            version: configStore.version,
        } as ClientTelemetryEvent;
        telemetryDataService.reportAsync(txEvent);
    };

    private shareInvite = () => {
        const { onInviteShared } = this.props;

        const { resourceId, resourceType } = this.props.contextStore!;

        const scenario = isTeamsAddonMode
            ? PredefinedAchievementScenario.TEAMS_ONBOARDING
            : PredefinedAchievementScenario.MEET_GENERAL;

        const achievement: AchivementsRequest = {
            scenario: scenario,
            checkpoint: PredefinedAchievementCheckpoint.MINIBAR_INVITE_CLICK,
            resourceId: resourceId!,
            resourceType: resourceType!,
        };

        achievementsService.postAsync(achievement);

        const txEvent = {
            correlationId: "N/A'",
            environment: configStore.environment,
            name: "spa_meetApp_inviteClick",
            sentryEventId: "N/A",
            silent: false,
            source: "meetApp",
            success: true,
            timeToComplete: 0,
            version: configStore.version,
        } as ClientTelemetryEvent;
        telemetryDataService.reportAsync(txEvent);

        onInviteShared();
    };

    private onHelpRequested = () => {
        this.props.contextStore!.requestTooltip(false);
    };

    private onMenuCollapse = () => {
        this.setState((state) => {
            return {
                collapsed: !state.collapsed,
            };
        });
    };

    private getPanelAddons = () => {
        const { classes } = this.props;
        const { addons } = this.props.panelStore!;

        const expanding = this.state.panelState === "expanding";
        const expanded = this.state.panelState === "expanded";

        return (
            <>
                {(expanded || expanding) && (
                    <Divider
                        orientation="vertical"
                        className={classes.divider}
                        style={{
                            marginRight: 16,
                        }}
                    />
                )}

                <List
                    component="nav"
                    className={
                        this.state.panelState !== "collapsed"
                            ? classes.panelAddons
                            : classes.hidden
                    }
                >
                    {addons.map((addon) => this.getPanelItem(addon, true))}
                </List>
            </>
        );
    };

    private getNavigationItem = (
        addonInfo: AddonRuntimeInfo,
        withText: boolean
    ) => {
        const {
            activeToolbar,
            toolbarIcon,
            inactiveToolbarItem,
        } = this.props.classes!;
        const { me } = this.props.configStore!;
        const { activeRoute, getRoute, navigate } = this.props.navigationStore!;

        const addonRoute = getRoute(addonInfo.identifier);
        const selectedAddon = activeRoute === addonInfo.identifier;

        let icon = addonInfo.host.icon;
        if (me?.theme === "dark") {
            if (addonInfo.host.iconDark) {
                icon = addonInfo.host.iconDark;
            }
        }

        return (
            <div
                className={
                    selectedAddon
                        ? `${toolbarIcon} ${activeToolbar}`
                        : `${toolbarIcon} ${inactiveToolbarItem}`
                }
                key={`cmd-${addonInfo.identifier}`}
            >
                <TooltipIconButton
                    button={true}
                    icon={icon as AppIconType}
                    placement="bottom"
                    title={getLocalizedText(addonInfo.title, me!.locale)}
                    onClick={() => navigate(addonRoute.path)}
                    open={false}
                />
                {withText && !this.state.collapsed && (
                    <Hidden xsDown={true}>
                        <Typography
                            variant="caption"
                            style={{ marginTop: -12 }}
                            color="textSecondary"
                        >
                            {getLocalizedText(addonInfo.title, me!.locale)}
                        </Typography>
                    </Hidden>
                )}
            </div>
        );
    };

    private getPanelItem = (panelAddon: PanelAddon, withText: boolean) => {
        const {
            activePanelAddon,
            drawerIconImage,
            toolbarIcon,
            inactiveToolbarItem,
        } = this.props.classes!;
        const { me } = this.props.configStore!;
        const { addonsInfo } = this.props.contextStore!;
        const { activeAddons } = this.props.panelStore!;

        const addonInfo = addonsInfo.find(
            (ai) => ai.identifier === panelAddon.addonIdentifier
        );
        if (!addonInfo) {
            throw new Error(
                "No panel addon found with id:" + panelAddon.addonIdentifier
            );
        }

        let icon = addonInfo.host.icon;
        if (me?.theme === "dark") {
            if (addonInfo.host.iconDark) {
                icon = addonInfo.host.iconDark;
            }
        }

        if (icon.indexOf("http") > -1) {
            return (
                <img
                    src={icon}
                    alt={getLocalizedText(addonInfo.title, me!.locale)}
                    className={drawerIconImage}
                />
            );
        }

        const selectedAddon = !!activeAddons.find(
            (p) => p.addonIdentifier === addonInfo.identifier
        );
        return (
            <Zoom
                key={`pnl-${addonInfo.identifier}`}
                in={!this.state.collapsed || selectedAddon}
                onEntering={() => this.setState({ panelState: "expanding" })}
                onEntered={() => this.setState({ panelState: "expanded" })}
                onExiting={() => this.setState({ panelState: "collapsing" })}
                onExited={() => this.setState({ panelState: "collapsed" })}
            >
                <div
                    className={
                        selectedAddon
                            ? `${toolbarIcon} ${activePanelAddon}`
                            : `${toolbarIcon} ${inactiveToolbarItem}`
                    }
                    key={`cmd-${addonInfo.identifier}`}
                >
                    <Badge
                        badgeContent={panelAddon.context}
                        color="secondary"
                        overlap="circle"
                        variant="standard"
                    >
                        <TooltipIconButton
                            button={true}
                            icon={icon as AppIconType}
                            placement="bottom"
                            title={getLocalizedText(
                                addonInfo.description || addonInfo.title,
                                me!.locale
                            )}
                            onClick={() =>
                                this.togglePanelInit(addonInfo.identifier)
                            }
                            open={false}
                        />
                    </Badge>
                    {withText && !this.state.collapsed && (
                        <Hidden xsDown={true}>
                            <Typography
                                variant="caption"
                                style={{ marginTop: -12 }}
                                color="textSecondary"
                            >
                                {getLocalizedText(addonInfo.title, me!.locale)}
                            </Typography>
                        </Hidden>
                    )}
                </div>
            </Zoom>
        );
    };

    private togglePanelInit = (identifier: string) => {
        const { toggle } = this.props.panelStore!;
        const { onSnapshotRequested } = this.props;

        this.setState({
            snapshotSelected: false,
        });

        toggle(identifier);
        onSnapshotRequested(false);
    };

    private showOnboardingTooltips = () => {
        if (
            this.lastTooltipRequest ===
            this.props.contextStore!.layout.tooltipRequested
        ) {
            return;
        }

        this.lastTooltipRequest = this.props.contextStore!.layout.tooltipRequested;
        sessionStorage.setItem(
            "last-tooltip-requested",
            JSON.stringify(this.lastTooltipRequest)
        );

        const seenCoachmarks = localStorage.getItem("meet-shell-tooltips");
        let timeout = 5000;
        if (seenCoachmarks === "v2") {
            timeout = 2000;
        } else {
            localStorage.setItem(
                "meet-shell-tooltips",
                new Date().toISOString()
            );
        }

        const highPriorityOnlyLoadRequest = this.lastTooltipRequest < 0;

        const self = this;

        this.sonarInTimeout = window.setTimeout(() => {
            self.setState({ sonarTooltip: true });
            this.sonarOutTimeout = window.setTimeout(() => {
                self.setState({ sonarTooltip: false });
            }, 2000);
        }, 100);

        if (!highPriorityOnlyLoadRequest) {
            this.feedbackInTimeout = window.setTimeout(() => {
                self.setState({ feedbackTooltip: true });
                this.feedbackOutTimeout = window.setTimeout(() => {
                    self.setState({ feedbackTooltip: false });
                }, timeout);
            }, 700);
        }

        if (!highPriorityOnlyLoadRequest) {
            this.settingInTimeout = window.setTimeout(() => {
                self.setState({ settingsTooltip: true });
                this.settingOutTimeout = window.setTimeout(() => {
                    self.setState({ settingsTooltip: false });
                }, timeout);
            }, 950);
        }

        this.inviteInTimeout = window.setTimeout(() => {
            self.setState({ inviteTooltip: true });
            this.inviteOutTimeout = window.setTimeout(() => {
                self.setState({ inviteTooltip: false });
            }, timeout);
        }, 1650);

        this.snapshotInTimeout = window.setTimeout(() => {
            self.setState({ snapshotTooltip: true });
            this.snapshotOutTimeout = window.setTimeout(() => {
                self.setState({ snapshotTooltip: false });
            }, timeout);
        }, 1900);

        this.dashboardInTimeout = window.setTimeout(() => {
            self.setState({
                dashboardTooltip: true,
            });
            this.dashboardOutTimeout = window.setTimeout(() => {
                self.setState({
                    dashboardTooltip: false,
                });
            }, timeout);
        }, 1300);

        localStorage.setItem("camera-preview-onboarded", "v2");
    };

    private clearTimeouts = () => {
        if (this.sonarInTimeout) {
            clearTimeout(this.sonarInTimeout);
            delete this.sonarInTimeout;
        }
        if (this.sonarOutTimeout) {
            clearTimeout(this.sonarOutTimeout);
            delete this.sonarOutTimeout;
        }
        if (this.feedbackInTimeout) {
            clearTimeout(this.feedbackInTimeout);
            delete this.feedbackInTimeout;
        }
        if (this.feedbackOutTimeout) {
            clearTimeout(this.feedbackOutTimeout);
            delete this.feedbackInTimeout;
        }
        if (this.settingInTimeout) {
            clearTimeout(this.settingInTimeout);
            delete this.settingInTimeout;
        }
        if (this.settingOutTimeout) {
            clearTimeout(this.settingOutTimeout);
            delete this.settingOutTimeout;
        }

        if (this.inviteInTimeout) {
            clearTimeout(this.inviteInTimeout);
            delete this.inviteInTimeout;
        }
        if (this.inviteOutTimeout) {
            clearTimeout(this.inviteOutTimeout);
            delete this.inviteOutTimeout;
        }
        if (this.dashboardInTimeout) {
            clearTimeout(this.dashboardInTimeout);
            delete this.dashboardInTimeout;
        }
        if (this.dashboardOutTimeout) {
            clearTimeout(this.dashboardOutTimeout);
            delete this.dashboardOutTimeout;
        }

        if (this.snapshotInTimeout) {
            clearTimeout(this.snapshotInTimeout);
            delete this.snapshotInTimeout;
        }
        if (this.snapshotOutTimeout) {
            clearTimeout(this.snapshotOutTimeout);
            delete this.snapshotOutTimeout;
        }
    };
}

export default withStyles(styles, { withTheme: true })(MiniBar);
